// shared/src/schemas/llm-call.ts
// Zod schema for `llm_calls` table (D1 migration 0001_init.sql:168-193,
// + 0034 source column + 0143 gateway_cache_status column). 20 columns; A2
// drift-detector enforces field-name parity.

import { z } from 'zod';

export const LlmCallStageEnum = z.enum(['M', 'P', 'A']);
export const LlmCallStatusEnum = z.enum(['success', 'error', 'timeout']);

export const LlmCallSchema = z.object({
  id:               z.string(),
  brief_id:         z.string().nullable(),
  step_name:        z.string(),                 // e.g. 'p.4.analyst-v2.NVDA'
  stage:            LlmCallStageEnum,
  model:            z.string(),                 // pricing.ts hard-fails on unknown
  prompt_text:      z.string(),
  prompt_tokens:    z.number().int().nonnegative(),
  response_text:    z.string(),                 // '' on error
  response_tokens:  z.number().int().nonnegative(),
  reasoning_text:   z.string(),
  reasoning_tokens: z.number().int().nonnegative(),
  cached_tokens:    z.number().int().nonnegative(),
  cost_usd:         z.number().nonnegative(),
  duration_ms:      z.number().int().nonnegative(),
  gateway_log_id:   z.string().nullable(),      // NULL on error before headers received
  status:           LlmCallStatusEnum,
  error_message:    z.string().nullable(),
  created_at:       z.string().datetime(),
  // 0034 — who triggered the call. qa-worker sets 'cron' | 'manual' (R4 trigger
  // column); other writers (chain executeLlmStep, macro-scorer) + pre-0034 rows
  // leave it NULL. Free TEXT in DDL; domain constrained at the type layer.
  source:           z.string().nullable(),
  // 0143 — AI Gateway's OWN response-cache indicator (`cf-aig-cache-status`
  // response header, documented values 'HIT' | 'MISS'). NOT the same thing as
  // `cached_tokens` above, which is DeepSeek's prompt_cache_hit_tokens (a
  // different, vendor-side cache) — see docs/investigations/2026-09-10-
  // ai-gateway-skip-cache-verification.md. Nullable: absent on pre-0143 rows
  // and on any throw-before-headers-read path. Free TEXT; domain documented,
  // not DDL-enforced (mirrors the 0034 `source` precedent).
  gateway_cache_status: z.string().nullable(),
});

export type LlmCall = z.infer<typeof LlmCallSchema>;
